import { useEffect, useState } from 'react';
import { Modal } from 'src/components/Modal';
import { Label } from 'src/components/Label';
import { Input } from 'src/components/Input';
import { Textarea } from 'src/components/Textarea';
import { SelectBox, Option } from 'src/components/SelectBox';
import { Button } from 'src/components/Button';
import { useNavigate } from 'react-router-dom';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { formatTimeLabel } from 'src/utils/date';
import { Loading } from 'src/components/Loading';
import { getTodayLocal } from '../../components/wizard/WizardContext';
import type { WorkflowContactWindow } from 'src/interfaces/workflows.interface';

type CreateCampaignDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onNext?: (data: {
    name: string;
    description: string;
    contactWindow: string;
  }) => void;
};

export const CreateCampaignDialog = ({
  isOpen,
  onClose,
  onNext,
}: CreateCampaignDialogProps) => {
  const navigate = useNavigate();
  const { get } = useAuthedRequest();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedContactWindowOption, setSelectedContactWindowOption] =
    useState<Option>({
      label: 'Select contact window',
      value: '',
    });
  const [contactWindowOptions, setContactWindowOptions] = useState<Option[]>(
    [],
  );
  const [contactWindows, setContactWindows] = useState<WorkflowContactWindow[]>(
    [],
  );
  const [isLoadingContactWindows, setIsLoadingContactWindows] = useState(false);

  const [selectedContactWindow, setSelectedContactWindow] =
    useState<WorkflowContactWindow | null>(null);

  useEffect(() => {
    (async () => {
      setIsLoadingContactWindows(true);
      try {
        const fetchedContactWindows = (await get(
          '/workflow_contact_windows',
        )) as WorkflowContactWindow[];
        const records = Array.isArray(fetchedContactWindows)
          ? fetchedContactWindows
          : [];
        setContactWindows(records);
        const optionItems = records.map((record) => ({
          label: record.label,
          value: record.id,
        }));
        setContactWindowOptions(optionItems);
        const firstRecord =
          records.length > 0 ? (records[0] as WorkflowContactWindow) : null;
        if (firstRecord) {
          setSelectedContactWindowOption({
            label: firstRecord.label,
            value: firstRecord.id,
          });
        }
        setSelectedContactWindow(firstRecord || null);
      } catch {
        setSelectedContactWindow(null);
      } finally {
        setIsLoadingContactWindows(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const weekdayLabels = ['Sun', 'Mon', 'Tues', 'Wed', 'Thur', 'Fri', 'Sat'];
  const daysLabel =
    Array.isArray(selectedContactWindow?.days) &&
    selectedContactWindow?.days.length
      ? selectedContactWindow.days
          .slice()
          .sort((a: number, b: number) => a - b)
          .map((d: number) => weekdayLabels[d])
          .join(', ')
      : 'Mon, Tues, Wed, Thurs, Fri';

  const startTime = selectedContactWindow?.startTime;
  const endTime = selectedContactWindow?.endTime;
  const startTimeLabel = startTime ? formatTimeLabel(startTime) : '9:00am';
  const endTimeLabel = endTime ? formatTimeLabel(endTime) : '7:00pm';

  const normalizeReferenceId = (value: string) =>
    value
      .replace(/\s+/g, '-')
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '');

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Create campaign"
      actionButton={
        <Button
          onClick={() => {
            onNext?.({
              name,
              description,
              contactWindow: String(selectedContactWindowOption.value),
            });
            if (name) {
              const normalized = normalizeReferenceId(name);
              const referenceId = `${normalized}-${getTodayLocal()}`;
              navigate('/beta/campaigns/new', {
                state: {
                  campaignInit: {
                    name,
                    description,
                    referenceId,
                    contactWindowId: String(selectedContactWindowOption.value),
                  },
                },
              });
            }
          }}
          disabled={!name}
        >
          Next
        </Button>
      }
      className="lg:max-w-[550px]"
    >
      <div className="space-y-4 pr-2">
        <div>
          <Label>Campaign name *</Label>
          <Input
            placeholder="Enter campaign name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div>
          <Label>Campaign description</Label>
          <Textarea
            placeholder="Enter campaign description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div>
          <Label>Campaign contact window</Label>
          {isLoadingContactWindows ? (
            <div className="flex gap-2 items-center py-2">
              <Loading size="6" />
              <span className="text-neutral-400 text-sm">
                Loading contact windows...
              </span>
            </div>
          ) : contactWindowOptions.length > 0 ? (
            <div className="flex gap-3 items-center w-full">
              <SelectBox
                className="w-full"
                options={contactWindowOptions}
                defaultValue={selectedContactWindowOption}
                onChange={(value) => {
                  const selectedOption = contactWindowOptions.find(
                    (o) => o.value === value,
                  );
                  if (selectedOption) {
                    setSelectedContactWindowOption(selectedOption);
                    const matchingRecord = contactWindows.find(
                      (record) => record.id === value,
                    );
                    setSelectedContactWindow(matchingRecord || null);
                  }
                }}
              />
            </div>
          ) : null}

          {selectedContactWindow ? (
            <div className="mt-3 rounded border p-3 text-sm text-neutral-700">
              <div className="flex items-center gap-2">
                <span className="font-medium">{daysLabel}</span>
                <span>•</span>
                <span>
                  {startTimeLabel} - {endTimeLabel}
                </span>
              </div>
              <div>
                <a
                  className="text-primary hover:underline cursor-pointer"
                  onClick={() => {
                    const params = new URLSearchParams(
                      selectedContactWindowOption.value
                        ? { id: String(selectedContactWindowOption.value) }
                        : {},
                    );
                    const search = params.toString();
                    navigate(
                      `/beta/campaigns/contact-window${search ? `?${search}` : ''}`,
                    );
                  }}
                >
                  Edit contact window
                </a>
              </div>
            </div>
          ) : (
            <div className="mt-3 rounded border p-3 text-sm text-neutral-700">
              <div className="flex items-center justify-between gap-2">
                <span>No contact window configured.</span>
                <Button
                  size="small"
                  onClick={() => navigate('/beta/campaigns/contact-window')}
                >
                  Create contact window
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default CreateCampaignDialog;
